import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ListingCard = ({ listing }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const title = listing?.properties?.['AI Product Name']?.title?.[0]?.text?.content || 
                listing?.properties?.['AI Product Name']?.title?.[0]?.plain_text || 
                'Untitled';
  const description = listing?.properties?.['Summary (short text)']?.rich_text?.[0]?.plain_text || 'No description';
  const logo = listing?.properties?.['Logo Image']?.url || '/logo192.png';
  const screenshot = listing?.properties?.['Screenshot Image']?.url || '';
  const rating = listing?.properties?.Rating?.number || null;
  const tags = listing?.properties?.Tags?.rich_text?.[0]?.plain_text || '';
  const apiAvailable = listing?.properties?.['API Available']?.checkbox || false;

  const formattedRating = rating !== null ? `${Math.round(rating * 10) / 10}/5` : 'Not rated';

  return (
    <Link 
      to={`/listings/${
        listing?.properties?.Slug?.rich_text?.[0]?.plain_text || listing.id
      }`}
      className="block bg-card rounded-card shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1"
    >
      <div className="p-6">
        <div className="relative aspect-video mb-4 rounded-card overflow-hidden">
          <img
            src={screenshot || logo}
            alt={`${title} screenshot`}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-background" />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <img
              src={logo}
              alt={`${title} logo`}
              className="w-10 h-10 rounded-logo object-cover"
            />
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-text">{title}</h2>
              {listing?.properties?.Featured?.checkbox && (
                <div className="bg-yellow-500/10 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-star w-6 h-6 text-yellow-500">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                </div>
              )}
              {listing?.properties?.['Recently Added']?.checkbox && (
                <div className="bg-blue-500/10 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-clock w-6 h-6 text-blue-500">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
              )}
              {listing?.properties?.['Top Listing']?.checkbox && (
                <div className="bg-green-500/10 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-trending-up w-6 h-6 text-green-500">
                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                    <polyline points="16 7 22 7 22 13"></polyline>
                  </svg>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {rating !== null && (
              <span className="flex items-center gap-1 text-yellow-500 font-medium">
                ⭐ {formattedRating}
              </span>
            )}
            <div className="flex flex-wrap gap-2">
              {tags.split(',').map((tag, i) => (
                <span 
                  key={i} 
                  className="px-2 py-1 text-sm font-medium text-muted bg-background rounded-md"
                  style={{ backgroundColor: `hsl(${i * 137.508}, 50%, 90%)` }}
                >
                  {tag.trim()}
                </span>
              ))}
              {apiAvailable && (
                <span className="px-2 py-1 text-sm font-medium text-green-600 bg-green-100 rounded-md flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  API Available
                </span>
              )}
            </div>
          </div>

          <p className="text-muted text-sm leading-relaxed">{description}</p>
        </div>
      </div>
    </Link>
  );
};

export default React.memo(ListingCard, (prevProps, nextProps) => {
  return prevProps.listing.id === nextProps.listing.id;
});
